export interface Vec2 {
  x: number;
  y: number;
}

/**
 * 한 번의 슈팅 입력. 클라이언트가 서버로 보내는 값이고,
 * 서버는 이걸 그대로 결정론 시뮬레이션에 넣는다.
 *
 * 좌표계 (테이블 기준, 단위 m):
 *   x — 테이블 폭 방향. 0 = 왼쪽 벽, table.width = 오른쪽 벽
 *   y — 경사 위 방향.  0 = 플레이어 쪽 끝, table.length = 먼 쪽 끝
 *   공은 +y(업슬로프)로 발사되고 중력에 의해 감속한다.
 */
export interface ShotInput {
  /** 발사 지점의 x 좌표. Play Line 뒤여야 한다. */
  x: number;
  /** 발사 각도 (radian). 0 = +y 정면, 양수 = 오른쪽. */
  angle: number;
  /** 발사 속도 (m/s). */
  power: number;
  /** 사이드 스핀. 진행 방향 수직으로 가해지는 가속 (m/s²). 좌우 휘기. */
  spin: number;
}

/** 서버가 검증하는 슈팅 파라미터 허용 범위. 벗어나면 클램프하지 않고 거부한다. */
export const SHOT_LIMITS = {
  /** 0 = 정면. ±60° */
  maxAngle: Math.PI / 3,
  minPower: 0.5,
  maxPower: 9,
  maxSpin: 4,
} as const;

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** 구조·범위 검증. 테이블 경계 검증은 서버에서 테이블 스펙과 함께 수행한다. */
export function validateShotInput(input: unknown): input is ShotInput {
  if (typeof input !== "object" || input === null) return false;
  const s = input as Record<string, unknown>;
  if (!isFiniteNumber(s.x) || !isFiniteNumber(s.angle)) return false;
  if (!isFiniteNumber(s.power) || !isFiniteNumber(s.spin)) return false;
  if (Math.abs(s.angle) > SHOT_LIMITS.maxAngle) return false;
  if (s.power < SHOT_LIMITS.minPower || s.power > SHOT_LIMITS.maxPower) return false;
  if (Math.abs(s.spin) > SHOT_LIMITS.maxSpin) return false;
  return true;
}
